<template>
  <div class="space-y-6">
    <!-- Header Section -->
    <div class="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold">Gestión de Razas</h1>
          <p class="text-indigo-100 mt-1">Administra las razas de cada tipo de animal</p>
        </div>
        <div class="hidden sm:block">
          <div class="bg-white/20 rounded-lg p-4">
            <svg class="h-12 w-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-indigo-100">
            <svg class="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Total Razas</p>
            <p class="text-2xl font-semibold text-gray-900">{{ breeds?.length || 0 }}</p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100">
            <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Razas de Perros</p>
            <p class="text-2xl font-semibold text-gray-900">{{ dogBreedsCount }}</p>
          </div>
        </div>
      </div>

      <div class="card p-6">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-orange-100">
            <svg class="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600">Razas de Gatos</p>
            <p class="text-2xl font-semibold text-gray-900">{{ catBreedsCount }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters and Actions -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <div class="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
          <!-- Search Section -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Buscar razas</label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                v-model="globalFilter"
                type="text"
                class="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm placeholder-gray-500 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="Escribe para buscar..."
              />
            </div>
          </div>
          
          <!-- Animal Type Filter -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Filtrar por tipo</label>
            <Dropdown
              v-model="selectedAnimalType"
              :options="animalTypes"
              optionLabel="name"
              optionValue="id"
              placeholder="Todos los tipos"
              class="w-full"
              showClear
            />
          </div>
        </div>
        
        <!-- Add Button -->
        <div class="flex-shrink-0">
          <button
            @click="router.push('/breeds/create')"
            class="inline-flex items-center px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            <svg class="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Agregar Raza
          </button>
        </div>
      </div>
    </div>

    <!-- DataTable with PrimeVue -->
    <div class="card">
      <DataTable
        v-model:filters="filters"
        :value="filteredBreeds"
        :loading="loading"
        paginator
        :rows="10"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        tableStyle="min-width: 50rem"
        filterDisplay="menu"
        :globalFilterFields="['name', 'description', 'animalType.name']"
        class="p-datatable-sm"
      >
        <template #header>
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-semibold text-gray-900">Lista de Razas</h2>
            <IconField iconPosition="left">
              <InputIcon>
                <i class="pi pi-search" />
              </InputIcon>
              <InputText v-model="filters['global'].value" placeholder="Buscar razas..." />
            </IconField>
          </div>
        </template>
        
        <template #empty>
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No hay razas</h3>
            <p class="mt-1 text-sm text-gray-500">Comienza agregando una nueva raza.</p>
          </div>
        </template>
        
        <template #loading>
          <div class="text-center py-8">
            <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p class="mt-2 text-gray-600">Cargando razas...</p>
          </div>
        </template>

        <Column field="name" header="Nombre" sortable>
          <template #body="{ data }">
            <div class="flex items-center">
              <div class="h-10 w-10 rounded-full overflow-hidden mr-3 cursor-pointer" @click="openImageModal(data.image || '/images/empty_image.png', data.name)">
                <img 
                  :src="data.image || '/images/empty_image.png'" 
                  :alt="data.name"
                  class="h-full w-full object-cover"
                  @error="$event.target.src = '/images/empty_image.png'"
                />
              </div>
              <div>
                <div class="text-sm font-medium text-gray-900">{{ data.name }}</div>
                <div class="text-sm text-gray-500">{{ data.pets?.length || 0 }} mascotas</div>
              </div>
            </div>
          </template>
        </Column>

        <Column field="animalType.name" header="Tipo de Animal" sortable>
          <template #body="{ data }">
            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="{
                    'bg-blue-100 text-blue-800': data.animalType?.name === 'Perro',
                    'bg-orange-100 text-orange-800': data.animalType?.name === 'Gato',
                    'bg-gray-100 text-gray-800': data.animalType?.name !== 'Perro' && data.animalType?.name !== 'Gato'
                  }">
              {{ data.animalType?.name || 'Sin tipo' }}
            </span>
          </template>
        </Column>

        <Column field="description" header="Descripción">
          <template #body="{ data }">
            <div class="text-sm text-gray-900 max-w-xs truncate" :title="data.description">
              {{ data.description || 'Sin descripción' }}
            </div>
          </template>
        </Column>

        <Column field="createdAt" header="Fecha de Creación" sortable>
          <template #body="{ data }">
            <span class="text-sm text-gray-500">
              {{ new Date(data.createdAt).toLocaleDateString('es-ES') }}
            </span>
          </template>
        </Column>

        <Column header="Acciones" :exportable="false">
          <template #body="{ data }">
            <div class="flex items-center space-x-2">
              <button
                @click="editBreed(data)"
                class="inline-flex items-center p-2 border border-transparent rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                style="background-color: #1d4ed8; color: #fff"
                title="Editar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                @click="deleteBreed(data)"
                class="inline-flex items-center p-2 border border-transparent rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                style="background-color: #be123c; color: #fff"
                title="Eliminar"
              >
                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Confirm Delete Modal -->
    <ConfirmModal
      :show="showConfirmModal"
      :title="'Eliminar Raza'"
      :message="`¿Estás seguro de que quieres eliminar la raza '${breedToDelete?.name}'? Esta acción eliminará también todas las mascotas asociadas y no se puede deshacer.`"
      :confirm-text="'Eliminar'"
      :cancel-text="'Cancelar'"
      @confirm="confirmDelete"
      @close="closeConfirmModal"
    />

    <!-- Image Modal -->
    <ImageModal
      :show="showImageModal"
      :image-url="selectedImage"
      :image-alt="selectedImageAlt"
      @close="closeImageModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useNotifications } from '../composables/useNotifications'
import { FilterMatchMode } from '@primevue/core/api'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Dropdown from 'primevue/dropdown'
import ConfirmModal from '../components/ConfirmModal.vue'
import ImageModal from '../components/ImageModal.vue'
import breedService from '../services/breedService'
import animalTypeService from '../services/animalTypeService'

const router = useRouter()
const authStore = useAuthStore()
const { showSuccess, showError } = useNotifications()

// Reactive data
const breeds = ref([])
const animalTypes = ref([])
const loading = ref(false)

// Filters
const globalFilter = ref('')
const selectedAnimalType = ref('')

// PrimeVue filters
const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS }
})

const showConfirmModal = ref(false)
const showImageModal = ref(false)
const breedToDelete = ref(null)
const selectedImage = ref('')
const selectedImageAlt = ref('')

// Computed properties
const dogBreedsCount = computed(() => {
  return breeds.value.filter(breed => breed.animalType?.name === 'Perro').length
})

const catBreedsCount = computed(() => {
  return breeds.value.filter(breed => breed.animalType?.name === 'Gato').length
})

const filteredBreeds = computed(() => {
  if (!selectedAnimalType.value) return breeds.value
  return breeds.value.filter(breed => breed.animalTypeId === selectedAnimalType.value)
})

// Watch for filter changes
watch(globalFilter, (newValue) => {
  filters.value.global.value = newValue
})

// Methods
const loadBreeds = async () => {
  try {
    loading.value = true
    const response = await breedService.getAllBreeds()
    breeds.value = response.data
  } catch (error) {
    console.error('Error loading breeds:', error)
    showError('Error al cargar las razas')
  } finally {
    loading.value = false
  }
}

const loadAnimalTypes = async () => {
  try {
    const response = await animalTypeService.getAllAnimalTypes()
    animalTypes.value = response.data
  } catch (error) {
    console.error('Error loading animal types:', error)
  }
}

const editBreed = (breed) => {
  router.push(`/breeds/${breed.id}/edit`)
}

const deleteBreed = (breed) => {
  breedToDelete.value = breed
  showConfirmModal.value = true
}

const closeConfirmModal = () => {
  showConfirmModal.value = false
  breedToDelete.value = null
}

const openImageModal = (imageUrl, imageAlt) => {
  selectedImage.value = imageUrl
  selectedImageAlt.value = imageAlt
  showImageModal.value = true
}

const closeImageModal = () => {
  showImageModal.value = false
  selectedImage.value = ''
  selectedImageAlt.value = ''
}

const confirmDelete = async () => {
  try {
    await breedService.deleteBreed(breedToDelete.value.id)
    showSuccess('Raza eliminada exitosamente')
    closeConfirmModal()
    await loadBreeds()
  } catch (error) {
    console.error('Error deleting breed:', error)
    showError('Error al eliminar la raza')
  }
}

// Initialize data
onMounted(async () => {
  await Promise.all([
    loadBreeds(),
    loadAnimalTypes()
  ])
})
</script>

<style scoped>
.card {
  @apply bg-white rounded-lg shadow-sm border border-gray-200;
}

:deep(.p-datatable) {
  border-radius: 0.5rem;
}

:deep(.p-datatable .p-datatable-header) {
  background: #f9fafb;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  padding: 1rem;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: #f9fafb;
  color: #374151;
  font-weight: 600;
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

:deep(.p-datatable .p-datatable-tbody > tr > td) {
  padding: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

:deep(.p-datatable .p-datatable-tbody > tr:hover) {
  background: #f9fafb;
}

:deep(.p-paginator) {
  background: #f9fafb;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  padding: 0.75rem 1rem;
}
</style>